import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDropzone } from "react-dropzone";
import {
  XMarkIcon,
  DocumentIcon,
  EyeIcon,
  LockClosedIcon,
  SparklesIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { Input, Button, CardHeader, Checkbox, Tabs } from "../components/ui";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  createTemplate,
  getTemplateById,
  updateTemplate,
  suggestTemplate,
  getTemplatesFromOtherCompanies,
  cloneTemplate,
  getOrganizationHasMultipleCompanies,
} from "../api/templates";
import { getSelfSignedDocuments } from "../api/user";
import { getDocument } from "../api/documents";
import DraggableRecipientsList from "../components/DraggableRecipientsList";
import { PDFDocument } from "pdf-lib";
import { getBase64FromUrl } from "../utils/general";
import PDFPreview from "../components/document/PDFPreview";
import { useAuth } from "../contexts/AuthContext";
import { usePageHeader } from "../contexts/PageHeaderContext";
import BreadCrumb from "../components/common/BreadCrumb";
import { v4 as uuidv4 } from "uuid";
import PagePermissionGuard from "../components/common/PagePermissionGuard";
import { getSignedUrl } from "../utils/signedUrl";

const RECIPIENT_COLORS = [
  "bg-blue-500",
  "bg-green-500",
  "bg-yellow-500",
  "bg-purple-500",
  "bg-pink-500",
  "bg-indigo-500",
  "bg-red-500",
  "bg-teal-500",
];

// Auto-resize textarea component
const AutoResizeTextarea = ({ minRows = 3, value, onChange, ...props }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto";

      // Calculate the height based on content
      const lineHeight =
        parseInt(window.getComputedStyle(textarea).lineHeight, 10) || 20; // Fallback to 20px if lineHeight is not available

      const minHeight = lineHeight * minRows;
      const scrollHeight = textarea.scrollHeight;

      // Set the height to either the scrollHeight or minHeight, whichever is larger
      textarea.style.height = `${Math.max(scrollHeight, minHeight)}px`;
    }
  }, [value, minRows]);

  return (
    <textarea ref={textareaRef} value={value} onChange={onChange} {...props} />
  );
};

const CreateTemplate = ({
  isFromUseTemplate = false,
  templateUseData = null,
  setFinalData = null,
}) => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [searchParams] = useSearchParams();
  const [files, setFiles] = useState([]);
  const [isOnlySigner, setIsOnlySigner] = useState(false);
  const [isSequential, setIsSequential] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [pdfBase64Url, setPdfBase64Url] = useState(null);
  const [activeTab, setActiveTab] = useState("upload");
  const [selfSignedDocuments, setSelfSignedDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [selectedSelfSignedDocument, setSelectedSelfSignedDocument] =
    useState(null);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [templatesFromOtherCompanies, setTemplatesFromOtherCompanies] =
    useState([]);
  const [byCompany, setByCompany] = useState([]);
  const [loadingCloneTemplates, setLoadingCloneTemplates] = useState(false);
  const [cloningTemplateId, setCloningTemplateId] = useState(null);
  const [hasMultipleCompanies, setHasMultipleCompanies] = useState(false);
  const [loadingHasMultipleCompanies, setLoadingHasMultipleCompanies] =
    useState(true);
  const { user, usage, organization } = useAuth();
  const { setHeaderContent } = usePageHeader();

  // Multi-step form: 1 = template selection, 2 = template details, 3 = recipients + email
  const [currentStep, setCurrentStep] = useState(() =>
    isFromUseTemplate ? 3 : templateId ? 2 : 1,
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/msword": [".doc"],
    },
    maxFiles: 10,
    onDrop: (acceptedFiles) => {
      setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
      formik.setFieldValue("files", [...files, ...acceptedFiles]);
      if (!formik.values.templateName) {
        formik.setFieldValue(
          "templateName",
          acceptedFiles[0].name.split(".")[0],
        );
      }
    },
  });

  const loadTemplatesFromOtherCompanies = async () => {
    try {
      setLoadingCloneTemplates(true);
      const response = await getTemplatesFromOtherCompanies();
      setTemplatesFromOtherCompanies(response.data || []);
      setByCompany(response.byCompany || []);
    } catch (error) {
      console.error("Error loading templates from other companies:", error);
      toast.error(error.message || "Failed to load templates");
      setTemplatesFromOtherCompanies([]);
      setByCompany([]);
    } finally {
      setLoadingCloneTemplates(false);
    }
  };

  const handleCloneTemplate = async (templateId) => {
    if (usage?.exceededTemplateCreation) {
      toast.error(
        "You have reached the template creation limit for your plan. Please upgrade to create more templates.",
      );
      return;
    }
    try {
      setCloningTemplateId(templateId);
      const response = await cloneTemplate(templateId);
      const newTemplate = response.data;
      toast.success("Template cloned successfully");
      navigate(
        `/dashboard/templates/configure-document/${newTemplate.documentId._id}/${newTemplate._id}`,
      );
    } catch (error) {
      toast.error(error.message || "Failed to clone template");
    } finally {
      setCloningTemplateId(null);
    }
  };

  const loadSelfSignedDocuments = async () => {
    try {
      setLoadingDocuments(true);
      const response = await getSelfSignedDocuments();
      const documents = response.data?.documents || [];
      setSelfSignedDocuments(documents);
      // Select the first document by default
      // if (documents.length > 0) {
      //   setSelectedSelfSignedDocument(documents[0]);
      // }
    } catch (error) {
      console.error("Error loading self-signed documents:", error);
      toast.error("Failed to load documents");
      setSelfSignedDocuments([]);
      setSelectedSelfSignedDocument(null);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const mergePdfs = async (pdfFiles) => {
    const mergedPdf = await PDFDocument.create();

    for (const file of pdfFiles) {
      const pdfBytes = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
      pages.forEach((page) => mergedPdf.addPage(page));
    }

    const mergedPdfBytes = await mergedPdf.save();
    return new File([mergedPdfBytes], `${pdfFiles[0].name}`, {
      type: "application/pdf",
    });
  };

  const setFormDefaultState = async (template) => {
    setTemplateData(template);

    formik.setValues({
      templateName: template.name,
      documentType: template.documentType || "",
      description: template.description,
      emailSubject: template.emailSubject,
      emailMessage: template.emailMessage,
      recipients: template.recipients,
      isSequential: template.isSequential,
      isOnlySigner: template.isOnlySigner,
    });
    setIsSequential(template.isSequential);
    setIsOnlySigner(template.isOnlySigner);

    const url = template.documentId.filePath;
    if (url) {
      try {
        const signedUrl = await getSignedUrl(url);
        const base64Pdf = await getBase64FromUrl(signedUrl);
        setPdfBase64Url(base64Pdf);
      } catch (error) {
        console.error("Error fetching signed URL:", error);
        toast.error("Failed to load document");
      }
    }
    setUploadedFile(template.documentId);
  };

  useEffect(() => {
    // Check if user has exceeded template creation limit (only for new template creation, not editing)
    if (!templateId && !isFromUseTemplate && usage?.exceededTemplateCreation) {
      toast.error(
        "You have reached the template creation limit for your plan. Please upgrade to create more templates.",
      );
      navigate("/dashboard/templates");
      return;
    }

    // Load document from query parameter (e.g., from AI generation)
    const documentId = searchParams.get("documentId");
    if (documentId && !templateId && !isFromUseTemplate) {
      const loadDocument = async () => {
        try {
          setIsLoading(true);
          const response = await getDocument(documentId);
          const document = response.data || response;

          // Set the document as uploaded file
          setUploadedFile(document);

          // Pre-fill AI-suggested fields if provided
          const aiDocumentType = searchParams.get("aiDocumentType");
          const aiTemplateName = searchParams.get("aiTemplateName");
          const aiDescription = searchParams.get("aiDescription");
          const aiEmailSubject = searchParams.get("aiEmailSubject");
          const aiEmailMessage = searchParams.get("aiEmailMessage");

          if (aiDocumentType) {
            formik.setFieldValue("documentType", aiDocumentType);
          }
          if (aiTemplateName && !formik.values.templateName) {
            formik.setFieldValue("templateName", aiTemplateName);
          }
          if (aiDescription && !formik.values.description) {
            formik.setFieldValue("description", aiDescription);
          }
          if (aiEmailSubject && !formik.values.emailSubject) {
            formik.setFieldValue("emailSubject", aiEmailSubject);
          }
          if (aiEmailMessage && !formik.values.emailMessage) {
            formik.setFieldValue("emailMessage", aiEmailMessage);
          }

          // Load the PDF preview (support both filePath and uploadUrl)
          const filePath = document.filePath || document.uploadUrl;
          if (filePath) {
            try {
              const signedUrl = await getSignedUrl(filePath);
              const base64Pdf = await getBase64FromUrl(signedUrl);
              setPdfBase64Url(base64Pdf);
              setActiveTab("preview");
            } catch (error) {
              console.error("Error fetching signed URL:", error);
              toast.error("Failed to load document preview");
            }
          }
        } catch (error) {
          console.error("Error loading document:", error);
          toast.error(error.message || "Failed to load document");
        } finally {
          setIsLoading(false);
        }
      };

      loadDocument();
      return;
    }

    // Load self-signed documents for new template creation
    if (!templateId && !isFromUseTemplate && !documentId) {
      loadSelfSignedDocuments();
    }

    if (templateId && !isFromUseTemplate) {
      const fetchTemplate = async () => {
        try {
          setIsLoading(true);
          const response = await getTemplateById(templateId);
          const template = response.data;

          await setFormDefaultState(template);
        } catch (error) {
          console.error("Error fetching template:", error);
          toast.error(error.message || "Failed to fetch template");
          navigate("/dashboard/templates");
        } finally {
          setIsLoading(false);
        }
      };

      fetchTemplate();
    }
    if (isFromUseTemplate) {
      const templateDataCallback = async () => {
        await setFormDefaultState(templateUseData);
      };
      templateDataCallback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    templateId,
    isFromUseTemplate,
    templateUseData,
    usage?.exceededTemplateCreation,
    navigate,
    searchParams,
  ]);

  useEffect(() => {
    if (activeTab === "cloneFromOther" && hasMultipleCompanies) {
      loadTemplatesFromOtherCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, hasMultipleCompanies]);

  // Fetch from backend whether org has multiple companies (not cached)
  useEffect(() => {
    if (!templateId && !isFromUseTemplate) {
      const fetchHasMultipleCompanies = async () => {
        try {
          setLoadingHasMultipleCompanies(true);
          const result = await getOrganizationHasMultipleCompanies();
          setHasMultipleCompanies(result);
        } catch {
          setHasMultipleCompanies(false);
        } finally {
          setLoadingHasMultipleCompanies(false);
        }
      };
      fetchHasMultipleCompanies();
    } else {
      setLoadingHasMultipleCompanies(false);
      setHasMultipleCompanies(false);
    }
  }, [templateId, isFromUseTemplate]);

  const handleSuggest = async () => {
    try {
      // Check if we have a file to analyze
      let fileToSend = null;

      if (files.length > 0) {
        fileToSend = files[0];
      } else if (selectedSelfSignedDocument) {
        // For self-signed documents, fetch the file from the URL
        try {
          const signedUrl = await getSignedUrl(
            selectedSelfSignedDocument.uploadUrl,
          );
          const response = await fetch(signedUrl);
          const blob = await response.blob();
          fileToSend = new File([blob], selectedSelfSignedDocument.fileName, {
            type: selectedSelfSignedDocument.contentType || blob.type,
          });
        } catch (error) {
          console.error("Error fetching file from URL:", error);
          toast.error("Failed to fetch file for suggestions");
          return;
        }
      } else if (
        uploadedFile &&
        (uploadedFile.filePath || uploadedFile.uploadUrl)
      ) {
        // For AI-generated or already-uploaded documents, fetch the file from its path
        const url = uploadedFile.filePath || uploadedFile.uploadUrl;
        try {
          const signedUrl = await getSignedUrl(url);
          const response = await fetch(signedUrl);
          const blob = await response.blob();
          fileToSend = new File(
            [blob],
            uploadedFile.fileName || "document.pdf",
            { type: uploadedFile.contentType || blob.type },
          );
        } catch (error) {
          console.error("Error fetching uploaded file for suggestions:", error);
          toast.error("Failed to fetch file for suggestions");
          return;
        }
      } else {
        toast.error("Please upload a file first to get suggestions");
        return;
      }

      setIsSuggesting(true);

      const formData = new FormData();
      formData.append("file", fileToSend);

      const response = await suggestTemplate(formData);
      const data = response.data || response;

      // Populate the form fields with the suggested values
      if (data.documentType) {
        formik.setFieldValue("documentType", data.documentType);
      }
      if (data.templateName || data.name) {
        formik.setFieldValue("templateName", data.templateName || data.name);
      }
      if (data.description) {
        formik.setFieldValue("description", data.description);
      }
      if (data.emailSubject) {
        formik.setFieldValue("emailSubject", data.emailSubject);
      }
      if (data.emailMessage) {
        formik.setFieldValue("emailMessage", data.emailMessage);
      }

      toast.success("Suggestions applied successfully");
    } catch (error) {
      console.error("Error getting suggestions:", error);
      toast.error(error.message || "Failed to get suggestions");
    } finally {
      setIsSuggesting(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      // Check if user has exceeded template creation limit (only for new template creation, not editing)
      if (
        !templateId &&
        !isFromUseTemplate &&
        usage?.exceededTemplateCreation
      ) {
        toast.error(
          "You have reached the template creation limit for your plan. Please upgrade to create more templates.",
        );
        return;
      }

      // return final data if from use template
      if (isFromUseTemplate) {
        setFinalData({
          ...values,
          recipients: values.recipients.map((recipient) => ({
            ...recipient,
            _id: recipient?._id || uuidv4(),
          })),
        });
        return;
      }

      setIsConverting(true);

      // When editing (templateId exists), only update metadata - no new document
      if (templateId && templateData) {
        await updateTemplate(templateId, {
          name: values.templateName,
          documentType: values.documentType || "",
          description: values.description,
          emailSubject: values.emailSubject,
          emailMessage: values.emailMessage,
          recipients: JSON.stringify(values.recipients),
          isSequential: values.isSequential,
          isOnlySigner: values.isOnlySigner,
        });
        toast.success("Template updated successfully");
        navigate(
          `/dashboard/templates/configure-document/${templateData.documentId._id}/${templateId}`,
        );
        return;
      }

      // Handle self-signed document selection
      if (selectedSelfSignedDocument) {
        const formData = new FormData();
        formData.append("uploadUrl", selectedSelfSignedDocument.uploadUrl);
        formData.append("uploadFileName", selectedSelfSignedDocument.fileName);
        formData.append("uploadFileSize", selectedSelfSignedDocument.fileSize);
        formData.append(
          "uploadContentType",
          selectedSelfSignedDocument.contentType,
        );
        formData.append("name", values.templateName);
        formData.append("documentType", values.documentType || "");
        formData.append("description", values.description);
        formData.append("emailSubject", values.emailSubject);
        formData.append("emailMessage", values.emailMessage);
        formData.append("recipients", JSON.stringify(values.recipients));
        formData.append("isSequential", values.isSequential);
        formData.append("isOnlySigner", values.isOnlySigner);

        const response = await createTemplate(formData);
        const documentId = response.data.documentId;
        toast.success("Template created successfully");
        navigate(
          `/dashboard/templates/configure-document/${documentId._id}/${response.data._id}`,
        );
        return;
      }

      // Handle already-uploaded document (e.g., from AI generation)
      if (uploadedFile && uploadedFile._id) {
        const formData = new FormData();
        formData.append("documentId", uploadedFile._id);
        formData.append("name", values.templateName);
        formData.append("documentType", values.documentType || "");
        formData.append("description", values.description);
        formData.append("emailSubject", values.emailSubject);
        formData.append("emailMessage", values.emailMessage);
        formData.append("recipients", JSON.stringify(values.recipients));
        formData.append("isSequential", values.isSequential);
        formData.append("isOnlySigner", values.isOnlySigner);

        const response = await createTemplate(formData);
        const documentId = response.data.documentId || uploadedFile;
        toast.success("Template created successfully");
        navigate(
          `/dashboard/templates/configure-document/${documentId._id || documentId}/${response.data._id}`,
        );
        return;
      }

      // Process files - PDF and DOC/DOCX only (backend handles DOC conversion)
      const pdfFiles = await Promise.all(
        files.map(async (file) => {
          if (file.type === "application/pdf") {
            return file;
          }
          // For DOC/DOCX files, backend handles conversion
          if (
            file.type ===
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
            file.type === "application/msword"
          ) {
            return file;
          }
          return file;
        }),
      );

      // Separate PDFs and DOCX files
      const pdfs = pdfFiles.filter((file) => file.type === "application/pdf");
      const docxs = pdfFiles.filter(
        (file) =>
          file.type ===
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
          file.type === "application/msword",
      );

      // If we have both PDFs and DOCX files, we need to handle them separately
      if (pdfs.length > 0 && docxs.length > 0) {
        // First merge all PDFs
        const mergedPdf = await mergePdfs(pdfs);

        // Create form data with merged PDF
        const formData = new FormData();
        formData.append("file", mergedPdf);
        formData.append("name", values.templateName);
        formData.append("documentType", values.documentType || "");
        formData.append("description", values.description);
        formData.append("emailSubject", values.emailSubject);
        formData.append("emailMessage", values.emailMessage);
        formData.append("recipients", JSON.stringify(values.recipients));
        formData.append("isSequential", values.isSequential);
        formData.append("isOnlySigner", values.isOnlySigner);

        // Send the request
        if (templateId) {
          await updateTemplate(templateId, {
            name: values.templateName,
            documentType: values.documentType || "",
            description: values.description,
            emailSubject: values.emailSubject,
            emailMessage: values.emailMessage,
            recipients: JSON.stringify(values.recipients),
            isSequential: values.isSequential,
            isOnlySigner: values.isOnlySigner,
          });
          toast.success("Template updated successfully");
          navigate(
            `/dashboard/templates/configure-document/${templateData.documentId._id}/${templateId}`,
          );
        } else {
          const response = await createTemplate(formData);
          const documentId = response.data.documentId;
          toast.success("Template created successfully");
          navigate(
            `/dashboard/templates/configure-document/${documentId._id}/${response.data._id}`,
          );
        }
      } else if (pdfs.length > 0) {
        // If we only have PDFs, merge them
        const mergedPdf = await mergePdfs(pdfs);

        const formData = new FormData();
        formData.append("file", mergedPdf);
        formData.append("name", values.templateName);
        formData.append("documentType", values.documentType || "");
        formData.append("description", values.description);
        formData.append("emailSubject", values.emailSubject);
        formData.append("emailMessage", values.emailMessage);
        formData.append("recipients", JSON.stringify(values.recipients));
        formData.append("isSequential", values.isSequential);
        formData.append("isOnlySigner", values.isOnlySigner);

        if (templateId) {
          await updateTemplate(templateId, {
            name: values.templateName,
            documentType: values.documentType || "",
            description: values.description,
            emailSubject: values.emailSubject,
            emailMessage: values.emailMessage,
            recipients: JSON.stringify(values.recipients),
            isSequential: values.isSequential,
            isOnlySigner: values.isOnlySigner,
          });
          toast.success("Template updated successfully");
          navigate(
            `/dashboard/templates/configure-document/${templateData.documentId._id}/${templateId}`,
          );
        } else {
          const response = await createTemplate(formData);
          const documentId = response.data.documentId;
          toast.success("Template created successfully");
          navigate(
            `/dashboard/templates/configure-document/${documentId._id}/${response.data._id}`,
          );
        }
      } else if (docxs.length > 0) {
        // If we only have DOCX files, send them directly
        const formData = new FormData();
        formData.append("file", docxs[0]); // Send the first DOCX file
        formData.append("name", values.templateName);
        formData.append("documentType", values.documentType || "");
        formData.append("description", values.description);
        formData.append("emailSubject", values.emailSubject);
        formData.append("emailMessage", values.emailMessage);
        formData.append("recipients", JSON.stringify(values.recipients));
        formData.append("isSequential", values.isSequential);
        formData.append("isOnlySigner", values.isOnlySigner);

        if (templateId) {
          await updateTemplate(templateId, {
            name: values.templateName,
            documentType: values.documentType || "",
            description: values.description,
            emailSubject: values.emailSubject,
            emailMessage: values.emailMessage,
            recipients: JSON.stringify(values.recipients),
            isSequential: values.isSequential,
            isOnlySigner: values.isOnlySigner,
          });
          toast.success("Template updated successfully");
          navigate(
            `/dashboard/templates/configure-document/${templateData.documentId._id}/${templateId}`,
          );
        } else {
          const response = await createTemplate(formData);
          const documentId = response.data.documentId;
          toast.success("Template created successfully");
          navigate(
            `/dashboard/templates/configure-document/${documentId._id}/${response.data._id}`,
          );
        }
      } else if (templateId) {
        await updateTemplate(templateId, {
          name: values.templateName,
          documentType: values.documentType || "",
          description: values.description,
          emailSubject: values.emailSubject,
          emailMessage: values.emailMessage,
          recipients: JSON.stringify(values.recipients),
          isSequential: values.isSequential,
          isOnlySigner: values.isOnlySigner,
        });
        toast.success("Template updated successfully");
        navigate(
          `/dashboard/templates/configure-document/${templateData.documentId._id}/${templateId}`,
        );
      }
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error(error.message || "Failed to save template");
    } finally {
      setIsConverting(false);
    }
  };

  const validationSchema = () => {
    return Yup.object({
      templateName: Yup.string()
        .required("Template name is required")
        .min(3, "Template name must be at least 3 characters"),
      description: Yup.string()
        .required("Description is required")
        .min(10, "Description must be at least 10 characters"),
      emailSubject: Yup.string(),
      emailMessage: Yup.string(),
      ...(!uploadedFile && !selectedSelfSignedDocument
        ? { files: Yup.array().of(Yup.mixed().required("File is required")) }
        : {}),
      recipients: Yup.array().when("isOnlySigner", {
        is: false,
        then: (schema) =>
          schema.min(1, "At least one recipient is required").of(
            Yup.object().shape({
              role: Yup.string()
                .required("Role is required")
                .min(2, "Role must be at least 2 characters"),
              isCC: Yup.boolean(),
              sequence: Yup.number().nullable(),
              color: Yup.string().required("Color is required"),
              email: !isFromUseTemplate
                ? Yup.string().email("Invalid email address").notRequired()
                : Yup.string()
                    .email("Invalid email address")
                    .required("Email is required"),
              name: !isFromUseTemplate
                ? Yup.string().notRequired()
                : Yup.string().required("Name is required"),
            }),
          ),
      }),
    });
  };
  const formik = useFormik({
    initialValues: {
      templateName: "",
      documentType: "",
      description: "",
      emailSubject: "",
      emailMessage: "",
      files: [],
      recipients: [
        {
          role: "",
          email: "",
          name: "",
          isCC: false,
          sequence: 1,
          color: RECIPIENT_COLORS[0], // Always use first color for single signer
        },
      ],
      isOnlySigner: false,
      isSequential: true,
      templateId: templateId,
    },
    validationSchema: validationSchema,
    // onSubmit: handleSubmit
    onSubmit: async (values, helpers) => {
      // if (isFromUseTemplate) {
      //   const hasEmptyEmails = values.recipients.some(
      //     (recipient) => !recipient.email?.trim()
      //   );

      //   if (hasEmptyEmails) {
      //     // Manually set touched and error for each recipient with missing email
      //     const updatedErrors = values.recipients.map((recipient) => {
      //       return !recipient.email?.trim() ? { email: 'Email is required' } : {};
      //     });

      //     helpers.setTouched({
      //       ...formik.touched,
      //       recipients: values.recipients.map(() => ({ email: true })),
      //     });

      //     helpers.setErrors({
      //       ...formik.errors,
      //       recipients: updatedErrors,
      //     });

      //     return; // Block submission
      //   }
      // }

      // Proceed to submit
      handleSubmit(values, helpers);
    },
  });

  const getRecipientError = (index, field) => {
    if (formik.touched.recipients && formik.errors.recipients) {
      if (Array.isArray(formik.errors.recipients)) {
        const recipientError = formik.errors.recipients[index];
        if (recipientError && typeof recipientError === "object") {
          return recipientError[field];
        }
      } else if (typeof formik.errors.recipients === "string") {
        return formik.errors.recipients;
      }
    }
    return null;
  };

  const isRecipientTouched = (index, field) => {
    formik.handleBlur(`recipients[${index}].${field}`);
    return formik.touched.recipients?.[index]?.[field];
  };

  const getNextAvailableColor = (recipients) => {
    const usedColors = new Set(recipients.map((r) => r.color));
    const availableColors = RECIPIENT_COLORS.filter(
      (color) => !usedColors.has(color),
    );
    if (availableColors.length === 0) {
      // If all colors are used, start reusing from the beginning
      return RECIPIENT_COLORS[0];
    }
    return availableColors[0];
  };

  const addRecipient = () => {
    const newRecipient = {
      email: "",
      name: "",
      isCC: false,
      sequence: formik.values.recipients.filter((r) => !r.isCC).length + 1,
      color: getNextAvailableColor(formik.values.recipients),
    };

    // Add new recipient and sort to move CC recipients to the end
    const updatedRecipients = [...formik.values.recipients, newRecipient];
    formik.setFieldValue("recipients", sortRecipients(updatedRecipients));
  };

  const handleOnlySignerChange = (e) => {
    const isChecked = e.target.checked;
    setIsOnlySigner(isChecked);
    formik.setFieldValue("isOnlySigner", isChecked);
    if (isChecked) {
      formik.setFieldValue("recipients", [
        {
          email: user?.email || "",
          name: user?.name || "",
          isCC: false,
          sequence: 1,
          role: user?.role?.name || "",
          color: RECIPIENT_COLORS[0], // Always use first color for single signer
        },
      ]);
    }
  };

  const handleSequentialChange = (e) => {
    const isChecked = e.target.checked;
    setIsSequential(isChecked);
    formik.setFieldValue("isSequential", isChecked);
  };

  const handleRecipientsChange = (updatedRecipients) => {
    formik.setFieldValue("recipients", updatedRecipients);
  };

  const handleCCChange = (index, e) => {
    const updatedRecipients = [...formik.values.recipients];
    updatedRecipients[index].isCC = e.target.checked;

    if (e.target.checked) {
      updatedRecipients[index].sequence = null;
    } else {
      updatedRecipients[index].sequence =
        formik.values.recipients.filter((r) => !r.isCC).length + 1;
    }

    formik.setFieldValue("recipients", sortRecipients(updatedRecipients));
  };

  const sortRecipients = (recipients) => {
    // Sort non-CC recipients by sequence, then append CC recipients
    const nonCCRecipients = recipients
      .filter((r) => !r.isCC)
      .sort((a, b) => a.sequence - b.sequence);

    const ccRecipients = recipients.filter((r) => r.isCC);

    return [...nonCCRecipients, ...ccRecipients];
  };

  const removeRecipient = (index) => {
    const updatedRecipients = [...formik.values.recipients];
    const removedRecipient = updatedRecipients[index];
    updatedRecipients.splice(index, 1);

    // Update sequence numbers for non-CC recipients
    if (!removedRecipient.isCC) {
      updatedRecipients
        .filter((r) => !r.isCC)
        .sort((a, b) => a.sequence - b.sequence)
        .forEach((r, idx) => {
          r.sequence = idx + 1;
        });
    }

    // Sort recipients to maintain CC recipients at the end
    formik.setFieldValue("recipients", sortRecipients(updatedRecipients));
  };

  const steps = [
    { id: 1, label: "Template", show: !templateId && !isFromUseTemplate },
    { id: 2, label: "Details", show: true },
    { id: 3, label: "Recipients & Email", show: true },
  ].filter((s) => s.show);

  const canProceedStep1 =
    files.length > 0 || selectedSelfSignedDocument || uploadedFile;

  // Set page header for Navbar
  useEffect(() => {
    const pageLabel = templateId
      ? "Update Template"
      : isFromUseTemplate
        ? "Use Template"
        : "Create New Template";
    setHeaderContent(
      <>
        <div>
          <BreadCrumb
            items={[
              { label: "Dashboard", href: "/dashboard" },
              { label: "Templates", href: "/dashboard/templates" },
              { label: pageLabel },
            ]}
            className="mb-2"
          />
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-gray-900">
            {pageLabel}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {isFromUseTemplate
              ? "Configure recipients and email for your document"
              : "Create a new template or update an existing one"}
          </p>
        </div>
      </>,
    );
    return () => setHeaderContent(null);
  }, [templateId, isFromUseTemplate, setHeaderContent]);

  return (
    <PagePermissionGuard
      module="templates"
      action="write"
      pageName="Create Template"
    >
      <div className="max-w-full mx-auto">
        {isLoading ? (
          <div className="flex justify-center items-center h-64 bg-white rounded-2xl shadow-sm">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary border-t-transparent" />
          </div>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden py-8">
              {/* Stepper */}
              <div className="mb-8 flex justify-start px-6 sm:px-8">
                <div className="inline-flex items-center justify-center gap-2 sm:gap-4">
                  {steps.map((step, idx) => (
                    <React.Fragment key={step.id}>
                      <button
                        type="button"
                        onClick={() => {
                          if (step.id === 1) setCurrentStep(1);
                          else if (
                            step.id === 2 &&
                            (canProceedStep1 || templateId || isFromUseTemplate)
                          )
                            setCurrentStep(2);
                          else if (step.id === 3) setCurrentStep(3);
                        }}
                        disabled={
                          step.id === 2 &&
                          !canProceedStep1 &&
                          !templateId &&
                          !isFromUseTemplate
                        }
                        className="flex items-center focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div
                          className={`flex items-center justify-center w-10 h-10 rounded-2xl text-sm font-semibold transition-colors duration-200 ${
                            currentStep === step.id
                              ? "bg-primary text-white"
                              : currentStep > step.id
                                ? "bg-secondary text-white"
                                : "bg-gray-200 text-gray-500"
                          }`}
                        >
                          {currentStep > step.id ? (
                            <CheckCircleIcon className="w-5 h-5" />
                          ) : (
                            step.id
                          )}
                        </div>
                        <span
                          className={`ml-2 text-sm font-medium hidden sm:inline ${
                            currentStep >= step.id
                              ? "text-gray-900"
                              : "text-gray-500"
                          }`}
                        >
                          {step.label}
                        </span>
                      </button>
                      {idx < steps.length - 1 && (
                        <div className="w-8 sm:w-12 h-0.5 bg-gray-200 flex-shrink-0" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              {/* Step 1: Template Selection */}
              {currentStep === 1 && !templateId && (
                <div className="px-6 sm:px-8">
                  <CardHeader
                    title="Choose your template"
                    subtitle="Upload a document, clone from another company, or use a self-signed document"
                  />
                  {/* Tab Navigation - show when org has multiple companies */}
                  {loadingHasMultipleCompanies ? (
                    <div className="h-12 bg-gray-200 rounded-xl animate-pulse mb-4" />
                  ) : (
                    hasMultipleCompanies && (
                      <div className="mb-4">
                        <Tabs
                          tabs={[
                            { value: "upload", label: "Upload" },
                            {
                              value: "cloneFromOther",
                              label: "Clone from other company",
                            },
                          ]}
                          value={activeTab}
                          onChange={(val) => {
                            setActiveTab(val);
                            setSelectedSelfSignedDocument(null);
                            setFiles([]);
                            formik.setFieldValue("files", []);
                          }}
                          variant="pill"
                        />
                      </div>
                    )
                  )}

                  {/* Upload Tab Content */}
                  {activeTab === "upload" && (
                    <div
                      {...getRootProps()}
                      className={`rounded-2xl p-10 text-center cursor-pointer transition-all duration-200 ${
                        isDragActive
                          ? "border-2 border-primary bg-primary/5 shadow-sm"
                          : "border-2 border-dashed border-gray-300 bg-gray-50/50 hover:border-primary/50 hover:bg-primary/5"
                      }`}
                    >
                      <input
                        {...getInputProps()}
                        type="file"
                        accept=".pdf,.docx,.doc"
                        multiple={false}
                      />
                      {files.length > 0 ? (
                        <div className="space-y-3">
                          {files.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between gap-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
                            >
                              <div className="flex items-center gap-4 min-w-0 flex-1">
                                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                                  <DocumentIcon className="h-6 w-6 text-primary" />
                                </div>
                                <div className="min-w-0 flex-1 text-left">
                                  <p className="text-sm font-semibold text-gray-900 truncate">
                                    {file.name}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {file.size >= 1024 * 1024
                                      ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                                      : `${(file.size / 1024).toFixed(1)} KB`}
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newFiles = [...files];
                                  newFiles.splice(index, 1);
                                  setFiles(newFiles);
                                  formik.setFieldValue("files", newFiles);
                                }}
                                className="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-danger hover:bg-danger/10 transition-colors duration-200"
                                aria-label="Remove file"
                              >
                                <XMarkIcon className="h-5 w-5" />
                              </button>
                            </div>
                          ))}
                          <p className="text-xs text-gray-500 pt-1">
                            Click or drag to replace
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex justify-center">
                            <div className="w-16 h-16 rounded-2xl bg-gray-200/80 flex items-center justify-center">
                              <DocumentIcon className="h-8 w-8 text-gray-400" />
                            </div>
                          </div>
                          <div>
                            <p className="font-heading text-lg font-semibold text-gray-900">
                              Drop your file here
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              or{" "}
                              <span className="font-medium text-primary">
                                browse
                              </span>{" "}
                              to upload
                            </p>
                          </div>
                          <p className="text-xs text-gray-500">
                            PDF, DOC or DOCX — max 10MB
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Clone from other company Tab Content */}
                  {activeTab === "cloneFromOther" && (
                    <div
                      className="border-2 border-gray-200 rounded-2xl p-6"
                      data-total-templates={templatesFromOtherCompanies?.length}
                    >
                      {loadingCloneTemplates ? (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                          <p className="text-gray-500">
                            Loading templates from other companies...
                          </p>
                        </div>
                      ) : byCompany.length === 0 ? (
                        <div className="text-center py-8">
                          <DocumentDuplicateIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <p className="text-gray-500">
                            No templates in other companies
                          </p>
                          <p className="text-sm text-gray-400 mt-1">
                            Create templates in your other companies first, then
                            clone them here
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-6">
                          <p className="text-sm text-gray-600">
                            Select a template from another company in your
                            organization to clone:
                          </p>
                          <div className="max-h-80 overflow-y-auto space-y-6">
                            {byCompany.map((companyGroup) => (
                              <div key={companyGroup._id} className="space-y-2">
                                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg bg-primary/10 text-primary">
                                    {companyGroup.name}
                                  </span>
                                  <span className="text-gray-400 text-xs font-normal">
                                    {companyGroup.templates?.length || 0}{" "}
                                    template(s)
                                  </span>
                                </h4>
                                <div className="grid gap-2 sm:grid-cols-2">
                                  {companyGroup.templates?.map((template) => (
                                    <div
                                      key={template._id}
                                      className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200 hover:border-primary/30 transition-colors duration-200"
                                    >
                                      <div className="min-w-0 flex-1">
                                        <p className="font-medium text-gray-900 truncate">
                                          {template.name}
                                        </p>
                                        <p className="text-xs text-gray-500 truncate">
                                          {template.documentId?.fileName || "—"}{" "}
                                          • {template.recipients?.length || 0}{" "}
                                          recipient(s)
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleCloneTemplate(template._id)
                                        }
                                        disabled={
                                          cloningTemplateId === template._id ||
                                          usage?.exceededTemplateCreation
                                        }
                                        className="ml-3 flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        {cloningTemplateId === template._id ? (
                                          <>
                                            <span className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                                            Cloning...
                                          </>
                                        ) : (
                                          <>
                                            <DocumentDuplicateIcon className="h-4 w-4" />
                                            Clone
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Self Signed Tab Content */}
                  {activeTab === "selfSigned" && (
                    <div className="border-2 border-gray-200 rounded-lg p-6">
                      {loadingDocuments ? (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                          <p className="text-gray-500">Loading documents...</p>
                        </div>
                      ) : selfSignedDocuments.length === 0 ? (
                        <div className="text-center py-8">
                          <DocumentIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <p className="text-gray-500">
                            No self-signed documents available
                          </p>
                          <p className="text-sm text-gray-400">
                            Upload documents in the Self Sign section first
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-gray-600 mb-4">
                            Select a document from your self-signed documents:
                          </p>
                          <div className="max-h-64 overflow-y-auto space-y-2">
                            {selfSignedDocuments.map((document) => {
                              const isSelected =
                                selectedSelfSignedDocument?._id ===
                                document._id;
                              return (
                                <div
                                  key={document._id}
                                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                    isSelected
                                      ? "border-primary bg-primary/5"
                                      : "border-gray-200 hover:border-gray-300"
                                  }`}
                                  onClick={() => {
                                    setSelectedSelfSignedDocument(document);
                                    formik.setFieldValue(
                                      "templateName",
                                      document.fileName,
                                    );
                                  }}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                      <DocumentIcon className="h-6 w-6 text-red-500" />
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">
                                          {document.fileName}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {new Date(
                                            document.createdAt,
                                          ).toLocaleDateString()}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <button
                                        type="button"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            if (document.uploadUrl) {
                                              const signedUrl =
                                                await getSignedUrl(
                                                  document.uploadUrl,
                                                );
                                              window.open(signedUrl, "_blank");
                                            }
                                          } catch (error) {
                                            console.error(
                                              "Error fetching signed URL:",
                                              error,
                                            );
                                            toast.error(
                                              "Failed to open document",
                                            );
                                          }
                                        }}
                                        className="p-1 text-gray-400 hover:text-primary transition-colors"
                                        title="View Document"
                                      >
                                        <EyeIcon className="h-4 w-4" />
                                      </button>
                                      {isSelected && (
                                        <div className="w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                                          <svg
                                            className="w-3 h-3 text-white"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                          >
                                            <path
                                              fillRule="evenodd"
                                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab !== "cloneFromOther" &&
                    formik.touched.files &&
                    formik.errors.files && (
                      <p className="mt-2 text-sm text-danger">
                        {formik.errors.files}
                      </p>
                    )}

                  <div className="mt-8 flex justify-end">
                    <Button
                      type="button"
                      size="lg"
                      rightIcon={ChevronRightIcon}
                      onClick={() => setCurrentStep(2)}
                      disabled={!canProceedStep1}
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2: Template Details */}
              {currentStep === 2 &&
                (activeTab !== "cloneFromOther" || templateId) && (
                  <div className="px-6 sm:px-8">
                    {pdfBase64Url && (
                      <div className="mb-8 p-6 rounded-2xl bg-gray-50/50 border border-gray-200">
                        <div className="flex flex-col sm:flex-row gap-6">
                          <PDFPreview
                            base64Url={pdfBase64Url}
                            uploadedFile={uploadedFile}
                          />
                          <div className="flex-1 min-w-0 flex flex-col justify-center">
                            <h4 className="font-heading text-xl font-bold text-gray-900 truncate">
                              {formik.values.templateName ||
                                "Untitled Template"}
                            </h4>
                            <p className="text-sm text-gray-500 mt-2 line-clamp-3">
                              {formik.values.description || "No description"}
                            </p>
                            <div className="mt-4 flex items-center gap-2">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-primary/10 text-primary">
                                Selected
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <CardHeader
                      title="Template details"
                      subtitle="Name your template and add a description"
                      actions={
                        <button
                          type="button"
                          onClick={handleSuggest}
                          disabled={
                            isSuggesting ||
                            (files.length === 0 &&
                              !selectedSelfSignedDocument &&
                              !uploadedFile)
                          }
                          className="relative overflow-hidden px-3 py-2 flex gap-2 items-center rounded-lg 
             bg-yellow text-gray-900 font-semibold
             transition-all duration-200 hover:bg-yellow-500
             disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Get AI suggestions"
                        >
                          <span
                            className="absolute inset-0 -translate-x-full 
                   bg-gradient-to-r from-transparent via-white/60 to-transparent 
                   animate-[shimmer_1.5s_infinite]"
                          ></span>
                          {isSuggesting ? (
                            <svg
                              className="animate-spin h-5 w-5"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                                fill="none"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                              />
                            </svg>
                          ) : (
                            <SparklesIcon className="h-5 w-5" />
                          )}
                          Auto Fill using AI
                        </button>
                      }
                    />
                    <div className="space-y-5">
                      <div className="relative">
                        <Input
                          label="Document Type"
                          name="documentType"
                          value={formik.values.documentType}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="e.g. NDA, Contract"
                          error={
                            formik.touched.documentType &&
                            formik.errors.documentType
                              ? formik.errors.documentType
                              : undefined
                          }
                        />
                      </div>

                      <Input
                        label="Template Name"
                        name="templateName"
                        value={formik.values.templateName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="e.g. NDA Template"
                        required
                        error={
                          formik.touched.templateName &&
                          formik.errors.templateName
                            ? formik.errors.templateName
                            : undefined
                        }
                      />

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Description <span className="text-danger">*</span>
                        </label>
                        <textarea
                          id="description"
                          name="description"
                          rows={3}
                          value={formik.values.description}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-4 py-3 rounded-xl border text-gray-900 placeholder-gray-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                            formik.errors.description &&
                            formik.touched.description
                              ? "border-danger"
                              : "border-gray-300"
                          }`}
                          placeholder="Describe this template"
                        />
                        {formik.touched.description &&
                          formik.errors.description && (
                            <p className="mt-1.5 text-xs font-medium text-danger">
                              {formik.errors.description}
                            </p>
                          )}
                      </div>

                      <div className="mt-8 flex justify-between">
                        {steps.some((s) => s.id === 1) ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="lg"
                            leftIcon={ChevronLeftIcon}
                            onClick={() => setCurrentStep(1)}
                          >
                            Back
                          </Button>
                        ) : (
                          <div />
                        )}
                        <Button
                          type="button"
                          size="lg"
                          rightIcon={ChevronRightIcon}
                          onClick={() => setCurrentStep(3)}
                        >
                          Continue
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

              {/* Step 3: Recipients & Email */}
              {currentStep === 3 && (
                <div className="px-6 sm:px-8">
                  <CardHeader
                    title="Recipients & Email"
                    subtitle="Add signers and configure email settings"
                  />
                  <div className="flex items-center gap-5">
                    <Checkbox
                      id="isOnlySigner"
                      name="isOnlySigner"
                      checked={isOnlySigner}
                      onChange={handleOnlySignerChange}
                      label="I'm the only signer"
                      disabled={isFromUseTemplate}
                    />
                    {!isOnlySigner &&
                      formik.values.recipients.filter((r) => !r.isCC).length >
                        1 &&
                      (organization?.planMeta?.["SEQUENTIAL_SIGNING"] ===
                      true ? (
                        <Checkbox
                          id="isSequential"
                          name="isSequential"
                          checked={isSequential}
                          onChange={handleSequentialChange}
                          label="Sign in sequence"
                          disabled={isFromUseTemplate}
                        />
                      ) : (
                        organization?.planMeta?.["SEQUENTIAL_SIGNING"] ===
                          false && (
                          <span className="flex items-center gap-2 text-sm text-gray-500">
                            <LockClosedIcon className="h-4 w-4" />
                            Sign in sequence
                          </span>
                        )
                      ))}
                  </div>
                  <div className="my-6 border-t border-gray-200" />
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading text-lg font-semibold text-gray-900">
                        Recipients
                      </h3>
                      {(!isOnlySigner ||
                        formik.values.recipients.length < 2) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={addRecipient}
                        >
                          + Add Recipient
                        </Button>
                      )}
                    </div>

                    <DraggableRecipientsList
                      recipients={formik.values.recipients}
                      isSequential={isSequential}
                      isOnlySigner={isOnlySigner}
                      onRecipientsChange={handleRecipientsChange}
                      onRemoveRecipient={removeRecipient}
                      onCCChange={handleCCChange}
                      getRecipientError={getRecipientError}
                      isRecipientTouched={isRecipientTouched}
                      formik={formik}
                    />

                    {formik.touched.recipients &&
                      typeof formik.errors.recipients === "string" && (
                        <div className="text-red-500 text-xs mt-1">
                          {formik.errors.recipients}
                        </div>
                      )}
                  </div>
                  <div className="my-6 border-t border-gray-200" />
                  <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">
                    Email Settings
                  </h3>
                  <div className="space-y-5">
                    <Input
                      label="Email Subject"
                      name="emailSubject"
                      value={formik.values.emailSubject}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Subject line for the email"
                      error={
                        formik.touched.emailSubject &&
                        formik.errors.emailSubject
                          ? formik.errors.emailSubject
                          : undefined
                      }
                    />
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Message
                      </label>
                      <AutoResizeTextarea
                        id="emailMessage"
                        name="emailMessage"
                        value={formik.values.emailMessage}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`w-full px-4 py-3 rounded-xl border text-gray-900 placeholder-gray-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none ${
                          formik.errors.emailMessage &&
                          formik.touched.emailMessage
                            ? "border-danger"
                            : "border-gray-300"
                        }`}
                        placeholder="Message to include in the email"
                        minRows={3}
                      />
                      {formik.touched.emailMessage &&
                        formik.errors.emailMessage && (
                          <p className="mt-1.5 text-xs font-medium text-danger">
                            {formik.errors.emailMessage}
                          </p>
                        )}
                    </div>
                  </div>

                  <div className="mt-8 flex justify-between">
                    {steps.some((s) => s.id === 2) ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="lg"
                        leftIcon={ChevronLeftIcon}
                        onClick={() => setCurrentStep(2)}
                      >
                        Back
                      </Button>
                    ) : (
                      <div />
                    )}
                    <Button
                      type="submit"
                      size="lg"
                      rightIcon={ChevronRightIcon}
                      disabled={isConverting}
                    >
                      {isConverting
                        ? "Converting..."
                        : isFromUseTemplate
                          ? "Continue"
                          : "Next"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </form>
        )}
      </div>
    </PagePermissionGuard>
  );
};

export default CreateTemplate;
